import React, { useState, useEffect, useCallback } from 'react';
import { loadCachedImage } from '@/utils/image-cache';
import { isValidImageUrl } from '@/utils/url-helpers';

interface CachedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onLoad' | 'onError'> {
  src: string;
  fallbackUrl?: string;
  alt: string;
  onLoad?: (event?: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: (error?: string) => void;
  loadingClassName?: string;
  errorClassName?: string;
  retries?: number;
  timeout?: number;
}

export const CachedImage: React.FC<CachedImageProps> = ({
  src,
  fallbackUrl,
  alt,
  onLoad,
  onError,
  loadingClassName = '',
  errorClassName = '',
  retries = 3,
  timeout = 10000,
  className = '',
  style = {},
  ...imgProps
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [imageSrc, setImageSrc] = useState<string>('');
  const [error, setError] = useState<string>('');

  const loadImage = useCallback(async () => {
    if (!src) {
      setImageState('error');
      setError('No source URL provided');
      onError?.('No source URL provided');
      return;
    }

    if (!isValidImageUrl(src)) {
      console.warn('Invalid image URL format:', src);
      setImageState('error');
      setError('Invalid URL format');
      onError?.('Invalid URL format');
      return;
    }

    setImageState('loading');
    setError('');

    try {
      const result = await loadCachedImage(src, {
        retries,
        timeout,
        fallbackUrl
      });

      if (result.success && result.url) {
        setImageSrc(result.url);
        setImageState('loaded');
        onLoad?.();
      } else {
        setImageState('error');
        setError(result.error || 'Failed to load image');
        onError?.(result.error || 'Failed to load image');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setImageState('error');
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [src, fallbackUrl, retries, timeout, onLoad, onError]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // Handle image load success
  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    if (imageState !== 'loaded') {
      setImageState('loaded');
      onLoad?.(event);
    }
  }, [imageState, onLoad]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    if (imageState !== 'error') {
      setImageState('error');
      setError('Image failed to load');
      onError?.('Image failed to load');
    }
  }, [imageState, onError]);

  // Render loading state
  if (imageState === 'loading') {
    return (
      <div 
        className={`bg-gray-200 animate-pulse flex items-center justify-center ${loadingClassName} ${className}`}
        style={style}
        {...(imgProps as any)}
      >
        <div className="text-gray-400 text-xs">Loading...</div>
      </div>
    );
  }

  // Render error state
  if (imageState === 'error') {
    return (
      <div 
        className={`bg-gray-200 flex items-center justify-center ${errorClassName} ${className}`}
        style={style}
        title={`Failed to load image: ${error}`}
        {...(imgProps as any)}
      >
        <div className="text-gray-400 text-xs text-center p-2">
          <div>Image</div>
          <div>Error</div>
        </div>
      </div>
    );
  }

  // Render loaded image
  return (
    <img
      {...imgProps}
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onLoad={handleImageLoad}
      onError={handleImageError}
    />
  );
};
